import * as React from "react";
import { ChevronUp, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

export interface NumberInputProps
  extends Omit<React.ComponentProps<"input">, "type" | "value" | "onChange" | "step"> {
  value: number | "";
  onValueChange: (value: number | "") => void;
  min?: number;
  max?: number;
  step?: number;
}

/**
 * Themed number input with custom stepper chevrons (the native browser spinner
 * is hidden). Click or hold the chevrons to step; typing still works and the
 * value is clamped to [min, max].
 */
const NumberInput = React.forwardRef<HTMLInputElement, NumberInputProps>(
  ({ className, value, onValueChange, min, max, step = 1, disabled, ...props }, ref) => {
    const holdTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
    const holdInterval = React.useRef<ReturnType<typeof setInterval> | null>(null);

    const clamp = (n: number) => {
      if (min !== undefined && n < min) return min;
      if (max !== undefined && n > max) return max;
      return n;
    };

    const nudge = (dir: 1 | -1) => {
      const base = value === "" || Number.isNaN(value as number) ? (min ?? 0) : (value as number);
      onValueChange(clamp(base + dir * step));
    };

    const startHold = (dir: 1 | -1) => {
      if (disabled) return;
      nudge(dir);
      holdTimer.current = setTimeout(() => {
        holdInterval.current = setInterval(() => nudge(dir), 70);
      }, 350);
    };

    const stopHold = () => {
      if (holdTimer.current) clearTimeout(holdTimer.current);
      if (holdInterval.current) clearInterval(holdInterval.current);
      holdTimer.current = null;
      holdInterval.current = null;
    };

    React.useEffect(() => stopHold, []);

    const atMax = max !== undefined && value !== "" && (value as number) >= max;
    const atMin = min !== undefined && value !== "" && (value as number) <= min;

    return (
      <div className={cn("relative h-10", className)}>
        <input
          ref={ref}
          type="number"
          inputMode="numeric"
          value={value}
          disabled={disabled}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === "") return onValueChange("");
            const n = Number(raw);
            if (!Number.isNaN(n)) onValueChange(n);
          }}
          onBlur={(e) => {
            // Re-clamp on blur so out-of-range typed values snap back.
            if (value !== "") onValueChange(clamp(value as number));
            props.onBlur?.(e);
          }}
          className={cn(
            "flex h-full w-full rounded-md border border-input bg-background pl-3 pr-8 py-1 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
            "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
          )}
          {...props}
        />
        <div className="absolute right-1 top-1 bottom-1 flex flex-col w-6 select-none">
          <button
            type="button"
            tabIndex={-1}
            aria-label="Increment"
            disabled={disabled || atMax}
            onMouseDown={() => startHold(1)}
            onMouseUp={stopHold}
            onMouseLeave={stopHold}
            onTouchStart={(e) => { e.preventDefault(); startHold(1); }}
            onTouchEnd={stopHold}
            className="flex-1 flex items-center justify-center rounded-t-[5px] text-muted-foreground hover:text-foreground hover:bg-muted/60 active:bg-muted disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
          >
            <ChevronUp className="h-3 w-3" />
          </button>
          <button
            type="button"
            tabIndex={-1}
            aria-label="Decrement"
            disabled={disabled || atMin}
            onMouseDown={() => startHold(-1)}
            onMouseUp={stopHold}
            onMouseLeave={stopHold}
            onTouchStart={(e) => { e.preventDefault(); startHold(-1); }}
            onTouchEnd={stopHold}
            className="flex-1 flex items-center justify-center rounded-b-[5px] text-muted-foreground hover:text-foreground hover:bg-muted/60 active:bg-muted disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
          >
            <ChevronDown className="h-3 w-3" />
          </button>
        </div>
      </div>
    );
  },
);
NumberInput.displayName = "NumberInput";

export { NumberInput };
